import { change, DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    cardNumberFormat,
    cvvValidator,
    emailList,
    expirationDateFormat,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeHealthInformation } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface HealthInformationFormData {
    student: string;
    formId: string;

    patientName: string;
    dob: string;
    address: string;
    phone: string;
    otherSpecified: string;
    providerName: string;
    providerAddress: string;
    providerPhone: string;
    providerFax: string;
    signature: string;
    todayDate: string;
    parentSignature: string;
    relationToPatient: string;
}

interface HealthInformationFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<HealthInformationFormData, HealthInformationFormProps>;

const validators: Validators<HealthInformationFormData> = {
    patientName: [maxLength({ max: 255 })],
    dob: [maxLength({ max: 255 })],
    address: [maxLength({ max: 255 })],
    phone: [maxLength({ max: 255 })],
    otherSpecified: [maxLength({ max: 255 })],
    providerName: [maxLength({ max: 255 })],
    providerAddress: [maxLength({ max: 255 })],
    providerPhone: [maxLength({ max: 255 })],
    providerFax: [maxLength({ max: 255 })],
    signature: [maxLength({ max: 255 })],
    todayDate: [maxLength({ max: 255 })],
    parentSignature: [maxLength({ max: 255 })],
    relationToPatient: [maxLength({ max: 255 })],
};

const HealthInformationForm: React.FunctionComponent<InjectedProps & HealthInformationFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & HealthInformationFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA: Release of Health Information Authorization Form</h2>
            <div style={{ textAlign: 'center' }}></div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={3}
                wrapperConfig={{
                    label: '',
                }}
            />
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="patientName"
                    validate={validators.patientName}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Patient Name: ',
                    }}
                />
            </div>
            <div>
                <FieldDate
                    name="dob"
                    type="text"
                    date={true}
                    validate={validators.dob}
                    wrapperConfig={{
                        label: 'DOB: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="address"
                    validate={validators.address}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Address: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="phone"
                    validate={validators.phone}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Phone: ',
                    }}
                />
            </div>
            <div>
                I hereby authorize <span style={{ textDecoration: 'underline' }}>Manhattan Psychology Group, PC</span>{' '}
                to release and/or obtain the following information in writing and/or verbally for the purpose of
                educational, psychiatric, and/or medical health planning:
                <table>
                    <tr>
                        <td>
                            <div style={{ padding: '20px' }}>Psycho/Neuro/Social Evaluations</div>
                        </td>
                        <td>
                            <div style={{ padding: '20px' }}>Psychiatric / ER Evaluations</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div style={{ padding: '20px' }}>Medical History</div>
                        </td>
                        <td>
                            <div style={{ padding: '20px' }}>Educational / School Reports</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div style={{ padding: '20px' }}>Progress updates/reports</div>
                        </td>
                        <td>
                            <div style={{ padding: '20px' }}>
                                <FieldInput
                                    name="otherSpecified"
                                    validate={validators.otherSpecified}
                                    wrapperConfig={{
                                        classes: {
                                            label: 'fullSize',
                                        },
                                        label: 'Other as specified: ',
                                    }}
                                />
                            </div>
                        </td>
                    </tr>
                </table>
            </div>
            <div style={{ paddingTop: '40px' }}>
                <b>This authorization is to the specified provider written below:</b>
            </div>
            <div>
                <FieldInput
                    name="providerName"
                    validate={validators.providerName}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Name of Provider, Institution, and/or Agency: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="providerAddress"
                    validate={validators.providerAddress}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Address: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="providerPhone"
                    validate={validators.providerPhone}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Telephone: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="providerFax"
                    validate={validators.providerFax}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Fax: ',
                    }}
                />
            </div>
            <div>
                I/we hereby authorize the release and exchange of the above information with the specified provider and{' '}
                <span style={{ textDecoration: 'underline' }}>Manhattan Psychology Group, PC</span> I/we understand that
                this consent may be revoked at any time in writing and will expire{' '}
                <span style={{ textDecoration: 'underline' }}>one year from the date signed below</span>. I understand
                that the above information after it has been released will be held in a confidential manner, and is not
                authorized for further release to a third party.
            </div>

            <div style={{ paddingTop: '40px' }}>
                <FieldInput
                    name="signature"
                    validate={validators.signature}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Patient Signature: ',
                    }}
                />
            </div>
            <div>
                <FieldDate
                    name="todayDate"
                    type="text"
                    date={true}
                    validate={validators.todayDate}
                    wrapperConfig={{
                        label: 'Date: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="parentSignature"
                    validate={validators.parentSignature}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Parent/Legal Guardian Signature: ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="relationToPatient"
                    validate={validators.relationToPatient}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Relationship to Patient: ',
                    }}
                />
            </div>
            <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
                Please fax the above requested information to 212-500-0007
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<HealthInformationFormData, HealthInformationFormProps>({
    form: 'health-information-form',
    enableReinitialize: true,
    onSubmit: async (data: HealthInformationFormData, dispatch: Dispatch<any>, props: HealthInformationFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeHealthInformation({ ...data }, dispatch);
    },
    onChange: async (
        values: Partial<HealthInformationFormData>,
        dispatch: Dispatch<any>,
        props: any,
        previousValues: Partial<HealthInformationFormData>,
    ) => {},
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<HealthInformationFormData, HealthInformationFormProps, string>,
    ) {
        props.setIsLoader(false);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(HealthInformationForm);
